import { useCallback, useRef, useState } from 'react';

export type StoredEndpoint = {
  id: string;
  url: string;
  label?: string;
  createdAt: string;
};

const STORAGE_KEY = 'saved_endpoints_v1';

const readStored = (): StoredEndpoint[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredEndpoint[]) : [];
  } catch (err) {
    console.warn('Failed to read saved endpoints from localStorage', err);
    return [];
  }
};

export function useEndpoints() {
  const [endpoints, setEndpoints] = useState<StoredEndpoint[]>(readStored);

  /**
   * Mirror of the list as it is being committed.
   *
   * Every mutation below persists *synchronously* through `commit()` rather
   * than from a `useEffect`, because a caller can add an endpoint and then
   * unmount this component in the same React commit (Home saves a generated
   * endpoint and immediately navigates to the inspector). React never runs
   * the effect for a component that unmounted in that commit, so an
   * effect-only writer loses the entry — the list stayed empty forever.
   *
   * The ref also makes two mutations fired before a re-render compose instead
   * of clobbering each other.
   */
  const endpointsRef = useRef(endpoints);

  const commit = useCallback((next: StoredEndpoint[]) => {
    endpointsRef.current = next;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (err) {
      console.warn('Failed to persist saved endpoints to localStorage', err);
    }
    setEndpoints(next);
  }, []);

  const addEndpoint = useCallback((url: string, label?: string) => {
    const newEp: StoredEndpoint = {
      id: typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : String(Date.now()),
      url,
      label,
      createdAt: new Date().toISOString(),
    };
    commit([newEp, ...endpointsRef.current]);
    return newEp;
  }, [commit]);

  const removeEndpoint = useCallback((id: string) => {
    commit(endpointsRef.current.filter(e => e.id !== id));
  }, [commit]);

  /**
   * Drop several saved entries in one write.
   *
   * The bulk path is the same promise as the single-row one: only the ids
   * handed in are dropped, everything else in the list is written back
   * untouched — in one commit, so a page that removes a group cannot leave the
   * stored list half-updated.
   */
  const removeEndpoints = useCallback((ids: string[]) => {
    const doomed = new Set(ids);
    commit(endpointsRef.current.filter(e => !doomed.has(e.id)));
  }, [commit]);

  /**
   * Give a saved endpoint a name a human chose.
   *
   * An empty or whitespace-only name clears the label rather than storing it,
   * so the row falls back to the slug — that is what a user who emptied the
   * field expects to see, and it keeps the `label || slug` render honest.
   * Only the label changes: the URL is what dedup keys on, so two endpoints
   * may carry the same name.
   */
  const renameEndpoint = useCallback((id: string, label: string) => {
    const name = label.trim();
    commit(endpointsRef.current.map(e =>
      e.id === id ? { ...e, label: name || undefined } : e,
    ));
  }, [commit]);

  const clearEndpoints = useCallback(() => {
    commit([]);
  }, [commit]);

  return { endpoints, addEndpoint, renameEndpoint, removeEndpoint, removeEndpoints, clearEndpoints };
}
